<template>
	<div class="my-benefits-page">
		<div class="benefit-page-heading">
			<div>
				<h2>我的权益</h2>
				<p>查看订单关联的功能权益及当前使用状态</p>
			</div>
			<div class="heading-decoration"><i class="el-icon-present"></i></div>
		</div>

		<div class="benefit-list-card">
			<tab-pane v-model="activeTab" :tabs="benefitTabs" :pane-style="tabPaneStyle">
				<template slot="extra">
					<div class="benefit-route-actions">
						<el-button icon="el-icon-document" @click="goToFeature('UserExport')">报告导出</el-button>
						<el-button icon="el-icon-star-off" @click="goToFeature('UserCollection')">我的收藏</el-button>
					</div>
				</template>

				<el-table v-loading="loading" :data="filteredOrders" border empty-text="当前分类暂无权益订单">
					<el-table-column prop="orderNo" label="订单编号" min-width="165"></el-table-column>
					<el-table-column label="商品信息" min-width="260">
						<template slot-scope="scope">
							<div class="order-products-cell">
								<div v-for="product in scope.row.products" :key="product.id" class="product-cell">
									<span class="product-icon"><i class="el-icon-s-goods"></i></span>
									<div>
										<strong>{{ product.productName }}</strong>
										<p>{{ product.productDesc }}</p>
									</div>
								</div>
							</div>
						</template>
					</el-table-column>
					<el-table-column label="数量" width="80" align="center">
						<template slot-scope="scope">
							<div class="product-quantities">
								<span v-for="product in scope.row.products" :key="product.id">× {{ product.quantity }}</span>
							</div>
						</template>
					</el-table-column>
					<el-table-column prop="orderTime" label="订单时间" min-width="165" align="center"></el-table-column>
					<el-table-column label="使用状态" width="110" align="center">
						<template slot-scope="scope">
							<div class="product-states">
								<div v-for="product in scope.row.products" :key="product.id">
									<span :class="['benefit-status', `is-${product.status}`]">
										<i></i>
										{{ getStatusText(product.status) }}
									</span>
								</div>
							</div>
						</template>
					</el-table-column>
					<el-table-column label="操作" width="145" align="center">
						<template slot-scope="scope">
							<div class="product-actions">
								<div v-for="product in scope.row.products" :key="product.id">
									<el-button class="view-benefit-button" @click="viewRemainingBenefit(scope.row, product)">
										查看剩余权益
									</el-button>
								</div>
							</div>
						</template>
					</el-table-column>
				</el-table>
			</tab-pane>
		</div>
	</div>
</template>

<script>
	import TabPane from '@/components/TabPane'
	import { queryMyGoodsBenefitOrders } from '@/api/mall'
	import { parseTime } from '@/utils'

	// 我的权益订单列表。
	export default {
		name: 'MyBenefits',
		components: { TabPane },
		data() {
			return {
				activeTab: 'all',
				loading: false,
				benefitOrders: [],
				tabPaneStyle: {
					'--tab-pane-header-padding-bottom': '12px',
					'--tab-pane-header-margin-bottom': '20px',
					'--tab-pane-item-font-size': '16px',
					'--tab-pane-indicator-bottom': '-12px'
				}
			}
		},
		computed: {
			// 页签数量从订单数据实时统计，后续对接接口后无需额外维护。
			benefitTabs() {
				return [
					{ name: '全部', value: 'all', badge: this.productCount },
					{ name: '待使用', value: 'pending', badge: this.countByStatus('pending') },
					{ name: '使用中', value: 'using', badge: this.countByStatus('using') },
					{ name: '使用完成', value: 'completed', badge: this.countByStatus('completed') }
				]
			},
			// 页签数量按商品统计，而不是按订单统计。
			productCount() {
				return this.benefitOrders.reduce((total, order) => total + order.products.length, 0)
			},
			// 按商品状态筛选，同时保留所属订单信息用于列表分组展示。
			filteredOrders() {
				if (this.activeTab === 'all') return this.benefitOrders
				return this.benefitOrders
					.map(order => ({
						...order,
						products: order.products.filter(product => product.status === this.activeTab)
					}))
					.filter(order => order.products.length > 0)
			}
		},
		created() {
			this.loadBenefitOrders()
		},
		methods: {
			async loadBenefitOrders() {
				this.loading = true
				try {
					const response = await queryMyGoodsBenefitOrders()
					const orders = Array.isArray(response.data) ? response.data : []
					// 后端 JSONObject 中的 Date 可能序列化为时间戳，进入页面时统一转换为可读时间。
					this.benefitOrders = orders.map(order => ({
						...order,
						orderTime: this.formatDateTime(order.orderTime),
						shipmentTime: this.formatDateTime(order.shipmentTime)
					}))
				} finally {
					this.loading = false
				}
			},
			formatDateTime(value) {
				if (value === undefined || value === null || value === '') return '-'
				let time = value
				if (typeof time === 'string' && /^\d+$/.test(time.trim())) {
					time = Number(time)
				}
				if (typeof time === 'number' && Number.isFinite(time) && String(Math.trunc(time)).length === 10) {
					time *= 1000
				}
				return parseTime(time, '{y}-{m}-{d} {h}:{i}:{s}') || String(value)
			},
			countByStatus(status) {
				return this.benefitOrders.reduce(
					(total, order) => total + order.products.filter(product => product.status === status).length,
					0
				)
			},
			getStatusText(status) {
				return { pending: '待使用', using: '使用中', completed: '使用完成' }[status] || '-'
			},
			// 权益详情由订单号和商品 ID 共同定位，避免同一订单下商品权益串用。
			viewRemainingBenefit(order, product) {
				this.$router.push({
					name: 'MyBenefitRemaining',
					query: {
						orderId: order.orderId,
						orderNo: order.orderNo,
						productId: product.id,
						orderGoodsId: product.orderGoodsId,
						productName: product.productName,
						productDesc: product.productDesc,
						quantity: product.quantity,
						status: product.status,
						benefitCount: product.benefitCount,
						orderTime: order.orderTime,
						shipmentTime: order.shipmentTime
					}
				})
			},
			// 报告导出和我的收藏已迁移为我的权益下的子功能。
			goToFeature(routeName) {
				this.$router.push({ name: routeName })
			}
		}
	}
</script>

<style lang="scss" scoped>
	.my-benefits-page {
		height: 100%;
		overflow-y: auto;
		overflow-x: hidden;
		font-size: 14px;
		background: #fff;
	}

	.benefit-page-heading {
		position: relative;
		display: flex;
		align-items: center;
		justify-content: space-between;
		padding: 22px 26px;
		overflow: hidden;
		color: #fff;
		background: linear-gradient(118deg, #246fc8, #438ed8 60%, #66a8e2);

		h2 {
			margin: 0 0 7px;
			font-size: 22px;
		}

		p {
			margin: 0;
			color: rgba(255, 255, 255, 0.8);
			font-size: 14px;
		}
	}

	.heading-decoration {
		position: absolute;
		right: 32px;
		color: rgba(255, 255, 255, 0.18);
		font-size: 74px;
		transform: rotate(-8deg);
	}

	.benefit-list-card {
		padding: 22px 24px 28px;
		overflow: hidden;
	}

	.benefit-route-actions {
		display: flex;
		gap: 10px;

		.el-button {
			margin: 0;
			padding: 8px 14px;
			border-color: #bed3e9;
			color: #3b73ad;
			background: #f7fbff;

			&:hover {
				border-color: #2671cb;
				color: #2671cb;
				background: #edf5fd;
			}
		}
	}

	.order-products-cell {
		overflow: hidden;
	}

	.product-cell {
		display: flex;
		align-items: center;
		min-height: 50px;
		padding: 6px 0;
		overflow: hidden;

		& + .product-cell {
			border-top: 1px dashed #e1e7ee;
		}

		.product-icon {
			display: flex;
			align-items: center;
			justify-content: center;
			width: 42px;
			height: 42px;
			margin-right: 11px;
			border-radius: 8px;
			color: #2873c9;
			font-size: 21px;
			background: linear-gradient(135deg, #e8f3ff, #dcecff);
			flex: 0 0 42px;
		}

		strong {
			color: #303b4b;
			font-size: 15px;
		}

		p {
			margin: 5px 0 0;
			color: #929ba8;
			font-size: 14px;
			line-height: 20px;
		}
	}

	.product-quantities {
		display: flex;
		flex-direction: column;
		justify-content: center;

		span {
			display: flex;
			align-items: center;
			justify-content: center;
			min-height: 63px;
			font-size: 14px;

			& + span {
				border-top: 1px dashed #e1e7ee;
			}
		}
	}

	.product-states,
	.product-actions {
		> div {
			display: flex;
			align-items: center;
			justify-content: center;
			min-height: 63px;

			& + div {
				border-top: 1px dashed #e1e7ee;
			}
		}
	}

	.benefit-status {
		display: inline-flex;
		align-items: center;
		padding: 4px 10px;
		border-radius: 13px;
		font-size: 14px;

		i {
			width: 6px;
			height: 6px;
			margin-right: 6px;
			border-radius: 50%;
			background: currentColor;
		}

		&.is-pending {
			color: #b77918;
			background: #fff3dc;
		}

		&.is-using {
			color: #2475ca;
			background: #e7f2fd;
		}

		&.is-completed {
			color: #778392;
			background: #edf0f3;
		}
	}

	.view-benefit-button {
		padding: 7px 11px;
		border-color: #8db8e4;
		color: #2671cb;
		background: #fff;

		&:hover {
			color: #fff;
			background: #2671cb;
		}
	}

		::v-deep {
			.tab-pane-container .tab-pane-header .tab-pane-navs .tab-pane-item {
				display: inline-flex;
				align-items: center;
				justify-content: center;
				line-height: 1;
			}

			.tab-pane-container .tab-pane-header .tab-pane-navs .tab-pane-item .tab-pane-item-badge {
				position: static;
			top: auto;
			right: auto;
			display: inline-flex;
			align-items: center;
			justify-content: center;
			min-width: 20px;
			height: 18px;
			margin-left: 7px;
			padding: 0 5px;
			border: 1px solid #d7e5f3;
			border-radius: 9px;
			color: #6883a0;
			font-size: 11px;
			font-weight: 500;
			line-height: 16px;
			background: #f1f6fb;
			box-sizing: border-box;
		}

			.tab-pane-container .tab-pane-header .tab-pane-navs .tab-pane-item.active .tab-pane-item-badge {
			border-color: #bfd7ef;
			color: #2671cb;
			background: #eaf4ff;
		}

		.el-table th {
			color: #4d6075;
			background: #f3f7fb;
		}

		.el-table td,
		.el-table th {
			padding: 14px 0;
		}
	}
</style>
